import type { AuthProvider } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import { authService } from '../services/auth/authService';
import type { LoginCredentials, UserInfo } from '../types/auth';
import { ERROR_MESSAGES } from '../utils/constants';
import i18n, { SUPPORTED_LANGUAGES } from '../i18n/config';

/**
 * Push the authenticated user into Sentry so every event is attributed to
 * them. We send id + email + username only; the SDK is configured with
 * sendDefaultPii=false so no IP/cookies leak.
 */
const setSentryUser = (user: UserInfo | null): void => {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: String(user.id),
    email: user.email || undefined,
    username: user.username || undefined,
  });
};

/**
 * Apply the user's preferred language from their identity. If the user
 * never picked one explicitly (no `language` cached in localStorage), the
 * backend's `effective_language` (computed from preference → portfolio
 * default) is used.
 */
const applyIdentityLanguage = (cached: any): void => {
  const stored = localStorage.getItem('language');
  if (stored) return; // user already chose, respect it

  const candidate =
    cached?.app_user?.language || cached?.app_user?.effective_language;
  if (candidate && (SUPPORTED_LANGUAGES as readonly string[]).includes(candidate)) {
    i18n.changeLanguage(candidate);
    localStorage.setItem('language', candidate);
  }
};

/**
 * Refine Auth Provider
 * Implements the Refine AuthProvider interface to manage authentication state
 * Integrates with our authService for backend communication
 */
export const authProvider: AuthProvider = {
  /**
   * Login method
   * Authenticates user with credentials and stores tokens
   * @param params - Login credentials (username and password)
   * @returns Success response with redirect or error response
   */
  login: async (params: LoginCredentials) => {
    try {
      const { username, password } = params;

      // Validate input
      if (!username || !password) {
        return {
          success: false,
          error: {
            name: 'LoginError',
            message: ERROR_MESSAGES.LOGIN_REQUIRED,
          },
        };
      }

      // Call auth service to login
      await authService.login({ username, password });

      // Pull the fresh profile so we can apply the user's preferred language
      // even if no Spanish/English variant was cached locally.
      try {
        const me = await authService.getMe();
        applyIdentityLanguage(me);
        setSentryUser(me);
      } catch {
        // Non-fatal: language detection falls back to navigator/localStorage
      }

      return {
        success: true,
        redirectTo: '/',
      };
    } catch (error: any) {
      console.error('Login error in auth provider:', error);

      // Determine error type for better user feedback
      let errorName = 'LoginError';
      if (error.message === ERROR_MESSAGES.NETWORK_ERROR) {
        errorName = 'NetworkError';
      } else if (error.message === ERROR_MESSAGES.SERVER_ERROR) {
        errorName = 'ServerError';
      }

      return {
        success: false,
        error: {
          name: errorName,
          message: error.message || ERROR_MESSAGES.INVALID_CREDENTIALS,
        },
      };
    }
  },

  /**
   * Logout method
   * Clears authentication state and tokens
   * @returns Success response with redirect to login page
   */
  logout: async () => {
    try {
      // Call auth service to logout (blacklists token on backend)
      await authService.logout();
      setSentryUser(null);

      return {
        success: true,
        redirectTo: '/login',
      };
    } catch (error: any) {
      console.error('Logout error in auth provider:', error);
      setSentryUser(null);

      // Even if backend logout fails, we still want to clear local tokens
      // authService.logout() already handles this in its finally block
      return {
        success: true,
        redirectTo: '/login',
      };
    }
  },

  /**
   * Check method
   * Validates if user is authenticated by checking stored tokens
   * @returns Authentication status with optional redirect
   */
  check: async () => {
    try {
      // Check if user has valid tokens
      const isAuthenticated = authService.isAuthenticated();

      if (isAuthenticated) {
        return {
          authenticated: true,
        };
      }

      return {
        authenticated: false,
        redirectTo: '/login',
        logout: true,
      };
    } catch (error: any) {
      console.error('Auth check error:', error);

      return {
        authenticated: false,
        redirectTo: '/login',
        logout: true,
      };
    }
  },

  /**
   * Get permissions method
   * Returns user permissions (not implemented in current requirements)
   * @returns null (permissions not implemented yet)
   */
  getPermissions: async () => {
    // Permissions not implemented in current requirements
    // Can be extended in the future based on user_type or roles
    return null;
  },

  /**
   * Get identity method
   * Returns current user information
   * @returns User info object or null if not authenticated
   */
  getIdentity: async () => {
    try {
      // Check if user is authenticated first
      if (!authService.isAuthenticated()) {
        return null;
      }

      // First try to get cached user info
      const cachedUserInfo = authService.getCachedUserInfo();

      if (cachedUserInfo) {
        applyIdentityLanguage(cachedUserInfo);
        setSentryUser(cachedUserInfo);
        return {
          ...cachedUserInfo,
          name: cachedUserInfo.first_name && cachedUserInfo.last_name
            ? `${cachedUserInfo.first_name} ${cachedUserInfo.last_name}`
            : cachedUserInfo.username,
          avatar: cachedUserInfo.app_user?.avatar_url ?? undefined,
        };
      }

      // If no cached info, fetch from backend
      const userInfo = await authService.getMe();
      applyIdentityLanguage(userInfo);
      setSentryUser(userInfo);

      return {
        ...userInfo,
        name: userInfo.first_name && userInfo.last_name
          ? `${userInfo.first_name} ${userInfo.last_name}`
          : userInfo.username,
        avatar: userInfo.app_user?.avatar_url ?? undefined,
      };
    } catch (error: any) {
      console.error('Get identity error:', error);
      return null;
    }
  },

  /**
   * On error method
   * Handles authentication errors from API requests
   * @param error - Error object from API request
   * @returns Error response with optional redirect
   */
  onError: async (error: any) => {
    console.error('Auth error:', error);

    // Handle 401 Unauthorized errors
    if (error?.status === 401 || error?.statusCode === 401) {
      return {
        error: {
          name: 'UnauthorizedError',
          message: ERROR_MESSAGES.TOKEN_EXPIRED,
        },
        logout: true,
        redirectTo: '/login',
      };
    }

    // Handle 403 Forbidden errors
    if (error?.status === 403 || error?.statusCode === 403) {
      return {
        error: {
          name: 'ForbiddenError',
          message: ERROR_MESSAGES.UNAUTHORIZED,
        },
      };
    }

    // Handle network errors
    if (error?.message === ERROR_MESSAGES.NETWORK_ERROR) {
      return {
        error: {
          name: 'NetworkError',
          message: ERROR_MESSAGES.NETWORK_ERROR,
        },
      };
    }

    // Handle server errors
    if (error?.status >= 500 || error?.message === ERROR_MESSAGES.SERVER_ERROR) {
      return {
        error: {
          name: 'ServerError',
          message: ERROR_MESSAGES.SERVER_ERROR,
        },
      };
    }

    // Return generic error
    return {
      error: {
        name: 'Error',
        message: error?.message || 'An error occurred',
      },
    };
  },
};

export default authProvider;
